import React, { useState } from 'react';

import Modal from '../ui/Modal';
import Input from '../ui/Input';
import Button from '../ui/Button';
import Spinner from '../ui/Spinner';
import { useAuth } from '../../context/AuthContext';
import { useSystemInfo } from '../../context/SystemContext';
import { useToast } from '../ui/Toast';
import { parseApiError } from '../../utils/apiError';

const defaultEmail = require('../../environment/keys').user_default_email;
const defaultPw = require('../../environment/keys').user_dafault_pw;

export default function LoginModal({ isOpen, onClose, onSwitchToRegister, onSwitchToForgot }) {
  const { login, resendVerification } = useAuth();
  const { showRegistration } = useSystemInfo();
  const { addToast } = useToast();

  const [email, setEmail] = useState(defaultEmail);
  const [password, setPassword] = useState(defaultPw);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);
  const [resending, setResending] = useState(false);

  const reset = () => {
    setPassword(defaultPw);
    setSubmitting(false);
    setFormError(null);
    setFieldErrors({});
    setUnverifiedEmail(null);
    setResending(false);
  };

  const close = () => {
    reset();
    setEmail(defaultEmail);
    onClose();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);
    setFieldErrors({});
    setUnverifiedEmail(null);
    try {
      const me = await login(email.trim(), password);
      addToast(`Welcome back, ${me?.name || 'friend'}!`, { variant: 'success' });
      close();
    } catch (err) {
      const { formError: fe, fieldErrors: fld } = parseApiError(err);
      setFormError(fe);
      setFieldErrors(fld);
      setSubmitting(false);
      // Only the "unverified email" 403 offers a resend — a deactivated
      // account (also 403) has no use for it. Server sends a machine-
      // readable `reason` alongside the display message for this.
      if (err?.response?.data?.reason === 'unverified') {
        setUnverifiedEmail(email.trim());
      }
    }
  };

  const onResend = async () => {
    setResending(true);
    try {
      await resendVerification(unverifiedEmail);
      addToast('Verification email sent — check your inbox.', { variant: 'success' });
    } catch {
      addToast('Could not resend the verification email. Please try again.', { variant: 'error' });
    } finally {
      setResending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={close} title="Log in">
      <form className="ss-form" onSubmit={onSubmit} noValidate>
        {formError && <p className="ss-form__error" role="alert">{formError}</p>}
        {unverifiedEmail && (
          <p className="ss-authswitch">
            <button type="button" className="ss-linkbtn" onClick={onResend} disabled={resending}>
              {resending ? 'Sending…' : 'Resend verification email'}
            </button>
          </p>
        )}

        <Input
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={fieldErrors.email}
          required
        />
        <Input
          label="Password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={fieldErrors.password}
          required
        />

        <div style={{ textAlign: 'right' }}>
          <button type="button" className="ss-linkbtn" onClick={onSwitchToForgot}>
            Forgot password?
          </button>
        </div>

        <div className="ss-form__actions">
          <Button type="submit" disabled={submitting}>
            {submitting ? <Spinner label="Logging in" /> : 'Log in'}
          </Button>
          {showRegistration && (
            <p className="ss-authswitch">
              Need an account?{' '}
              <button type="button" className="ss-linkbtn" onClick={onSwitchToRegister}>
                Register
              </button>
            </p>
          )}
        </div>
      </form>
    </Modal>
  );
}
